import { Navigate } from "react-big-calendar";
import dayjs from "dayjs";
import { Flex, Typography } from "antd";
import { onlyTimeFun, toCapitalize, utcTimeToLocalPA } from "../../shared";

const { Text, Paragraph } = Typography;

const statusColors = {
  Pending: "#FFA500", // orange
  "Appointment scheduled": "#52c41a", // blue
  "Callback required": "#722ed1", // purple
  Called: "#FFD700", // green
  "No response": "#1677ff",
  "Not interested": "#f5222d",
};

const DraftCustomWeekView = ({
  date,
  events,
  onSelectEvent,
  onShowMore,
  handleShowMore,
}) => {
  const startOfWeek = dayjs(date).startOf("week");
  const endOfWeek = dayjs(date).endOf("week");
  const days = Array.from({ length: 7 }, (_, i) => startOfWeek.add(i, "day"));

  const weekEvents = events.filter((e) =>
    dayjs(e.start).isBetween(startOfWeek, endOfWeek, "day", "[]"),
  );

  const eventsByDay = days.map((day) =>
    events.filter((e) => dayjs(e.start).isSame(day, "day")),
  );

  const totalDraft = eventsByDay.reduce((sum, list) => sum + list.length, 0);

  return (
    <div className="custom-week-view">
      <div className="custom-week-header">
        <Text type="secondary">{totalDraft} Draft this week</Text>
        <a
          className="view-week-link"
          onClick={() => handleShowMore(weekEvents)}
        >
          📅 View week in drawer
        </a>
      </div>

      <div className="custom-week-grid">
        {days.map((day, i) => {
          const isToday = day.isSame(dayjs(), "day");
          const dayEvents = eventsByDay[i];
          const visible = dayEvents.slice(0, 3);
          const hidden = dayEvents.length - visible.length;

          return (
            <div key={i} className="custom-week-day">
              <div className="day-header">
                <div>
                  <div className={`dow ${isToday ? "today" : ""}`}>
                    {day.format("ddd").toUpperCase()}
                  </div>
                  <div className={`dom ${isToday ? "today" : ""}`}>
                    {day.format("D")}
                  </div>
                </div>
                {dayEvents.length > 0 && (
                  <span className="count">{dayEvents.length}</span>
                )}
              </div>

              <div className="day-events">
                {visible.map((ev) => (
                  <div
                    key={ev.id}
                    className="event-pill"
                    style={{ backgroundColor: statusColors[ev.status] }}
                    onClick={() => onSelectEvent(ev)}
                  >
                    <Flex className="w-100" gap={2} vertical>
                      <Flex justify="space-between" gap={5}>
                        <Paragraph
                          className="m-0 name"
                          ellipsis={{ rows: 1 }}
                          style={{ width: 100, textAlign: "left" }}
                        >
                          {toCapitalize(ev.title)}
                        </Paragraph>
                        <span className="time">
                          {ev?.alldata?.when_to_call_again
                            ? utcTimeToLocalPA(ev?.alldata?.when_to_call_again)
                            : dayjs(ev.start).format("hh:mm A")}
                        </span>
                      </Flex>
                      <Flex gap={2}>
                        <Paragraph
                          className="m-0 fs-10 text-white"
                          ellipsis={{ rows: 1 }}
                          style={{
                            width: "max-content",
                            textAlign: "left",
                            padding: "1px 7px",
                            borderRadius: 4,
                            border: "1px solid #d9d9d9",
                          }}
                        >
                          {toCapitalize(ev?.alldata?.services?.[0]?.name)}
                        </Paragraph>
                        {ev?.alldata?.services?.length > 1 && (
                          <span
                            style={{
                              fontSize: 10,
                              fontWeight: 500,
                              color: "#fff",
                              padding: "1px 7px",
                              borderRadius: 4,
                              border: "1px solid #d9d9d9",
                              whiteSpace: "nowrap",
                            }}
                          >
                            +{ev?.alldata?.services?.length - 1}
                          </span>
                        )}
                      </Flex>
                    </Flex>
                  </div>
                ))}
                {hidden > 0 && (
                  <div
                    className="more-link"
                    onClick={() => onShowMore(dayEvents, day.toDate())}
                  >
                    +{hidden} more
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

DraftCustomWeekView.range = (date) => {
  const start = dayjs(date).startOf("week");
  return Array.from({ length: 7 }, (_, i) => start.add(i, "day").toDate());
};

DraftCustomWeekView.navigate = (date, action) => {
  switch (action) {
    case Navigate.PREVIOUS:
      return dayjs(date).subtract(1, "week").toDate();
    case Navigate.NEXT:
      return dayjs(date).add(1, "week").toDate();
    default:
      return date;
  }
};

DraftCustomWeekView.title = (date) => {
  const start = dayjs(date).startOf("week");
  const end = start.add(6, "day");
  return `${start.format("MMM D")} – ${end.format("MMM D, YYYY")}`;
};

export { DraftCustomWeekView };
